// LICENSE_CODE ZON
'use strict'; /*jslint browser:true, es6:true, node:true, react:true*/
import {useEffect, useRef, useState} from 'react';

export interface i_viewport_margin {
    left?: number;
    top?: number;
    right?: number;
    bottom?: number;
}

/**
 * Hook to ensure an element stays within viewport bounds
 *
 * @param viewport_margin Minimum distance from viewport edges
 * @param deps Additional dependencies for the effect
 * @returns Object with element ref and computed shifts
 */
export const use_viewport_margin = (viewport_margin: i_viewport_margin|number,
    deps: any[] = [])=>
    {
    const element_ref = useRef<HTMLElement>(null);
    const [shift_x, set_shift_x] = useState(0);
    const [shift_y, set_shift_y] = useState(0);
    useEffect(()=>{
        if (!viewport_margin || !element_ref.current)
            return;
        const adjust_position = ()=>{
            const el = element_ref.current;
            if (!el)
                return;
            const rect = el.getBoundingClientRect();
            const v_height = window.innerHeight;
            const v_width = window.innerWidth;
            const margins = typeof viewport_margin === 'number' ?
                {
                    top: viewport_margin,
                    right: viewport_margin,
                    bottom: viewport_margin,
                    left: viewport_margin,
                } : viewport_margin;
            let v_shift = 0;
            if (margins.bottom && rect.bottom > v_height - margins.bottom)
                v_shift = v_height - margins.bottom - rect.bottom;
            else if (margins.top && rect.top < margins.top)
                v_shift = margins.top - rect.top;
            let h_shift = 0;
            if (margins.right && rect.right > v_width - margins.right)
                h_shift = v_width - margins.right - rect.right;
            else if (margins.left && rect.left < margins.left)
                h_shift = margins.left - rect.left;
            el.style.setProperty('--shift_y', `${v_shift}px`);
            el.style.setProperty('--shift_x', `${h_shift}px`);
            set_shift_x(h_shift);
            set_shift_y(v_shift);
        };
        const force_measure = ()=>{
            const el = element_ref.current;
            if (!el)
                return;
            const orig_opacity = el.style.opacity;
            const orig_visibility = el.style.visibility;
            const orig_display = el.style.display;
            el.style.opacity = '0';
            el.style.visibility = 'visible';
            if (orig_display === 'none')
                el.style.display = '';
            adjust_position();
            el.style.opacity = orig_opacity;
            el.style.visibility = orig_visibility;
            el.style.display = orig_display;
        };
        force_measure();
        window.addEventListener('resize', adjust_position);
        return ()=>{
            window.removeEventListener('resize', adjust_position);
            if (element_ref.current)
            {
                element_ref.current.style.removeProperty('--shift_y');
                element_ref.current.style.removeProperty('--shift_x');
            }
        };
    }, [viewport_margin, ...deps]);
    return {ref: element_ref, shift_x, shift_y};
};
